"use strict";
// The cadence vocabulary, once, for web, native and the test that holds the
// backend to it (#747).
//
// WHY IT IS SHARED. `RecurringTransaction.frequency`
// (`backend/apps/forecasting/models.py`) has five values, and every client
// surface used to re-spell them by hand. Each hand-copy drifted, and each drift
// shipped: mobile's Recurring screen rewrote `biweekly` as `"bi-weekly"` on the
// way out of a PATCH, so every biweekly cadence edit was rejected with no error
// shown; web's income form offered `semimonthly`, which is not a model choice,
// and silently stored `biweekly` instead, on the field that anchors every pay
// period; the same surface carried `semimonthly: 2` against the backend's 26/12,
// an 8.3% divergence in monthly-equivalent money. A wrong cadence string is a
// valid `string`, so none of that failed to compile. Now it does.
//
// TWO FIELDS, NOT ONE. `UserProfile.pay_frequency` is a DIFFERENT field with a
// different set: it also allows `semimonthly` and `irregular`, has no
// `quarterly` or `yearly`, and does not anchor pay periods. Merging the two is
// the plausible way to make this worse, so they are two types and two label maps
// here, each named for its field.
//
// THE LABELS ARE THE SERVER'S. `apps/debts/services/summary.py::_PAY_FREQ_LABEL`
// and `apps/financial_analysis/services/aggregation.py::_FREQUENCY_LABEL` print
// these strings, and several client maps are read only as a fallback for when
// the server sends none. A fallback that disagrees with the value it replaces is
// how one stream came to read "Bi-weekly" on Recurring and "Every 2 weeks" in
// onboarding and on Debt Eliminator (#755). `backend/tests/test_cadence_vocabulary.py`
// fails if this file and the server stop agreeing.
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAY_FREQUENCY_LABELS = exports.CADENCE_LABELS = exports.MONTHLY_FACTORS = exports.CADENCE_ORDER = void 0;
exports.monthlyFactor = monthlyFactor;
exports.cadenceLabel = cadenceLabel;
/** The five cadences, shortest period first. */
exports.CADENCE_ORDER = [
    "weekly",
    "biweekly",
    "monthly",
    "quarterly",
    "yearly",
];
/** Per-cycle conversion factors shared by the web and native clients. */
exports.MONTHLY_FACTORS = {
    weekly: 52 / 12,
    biweekly: 26 / 12,
    monthly: 1,
    quarterly: 1 / 3,
    yearly: 1 / 12,
};
/**
 * `semimonthly` (twice a month on two calendar days, CAL-001) is on the backend
 * model but not yet in `MonthlyCadence` / `CADENCE_LABELS` / `MONTHLY_FACTORS`:
 * mobile's sharedConstants test pins those maps, and mobile/ was out of scope
 * for the batch that added it. Until the mobile session widens them, these two
 * helpers carry it, and they are how both clients read a cadence. The backend
 * parity tests hold it as an explicit, self-expiring allowance.
 */
const SEMIMONTHLY = "semimonthly";
/** Unknown cadences are treated as monthly, matching the backend fallback. */
function monthlyFactor(frequency) {
    if (frequency === SEMIMONTHLY)
        return 2;
    return exports.MONTHLY_FACTORS[frequency] ?? 1;
}
/** How a `RecurringTransaction.frequency` is written for a user to read. */
exports.CADENCE_LABELS = {
    weekly: "Every week",
    biweekly: "Every 2 weeks",
    monthly: "Monthly",
    quarterly: "Every 3 months",
    yearly: "Yearly",
};
/** Label for a cadence, falling back to the raw value for anything unknown. */
function cadenceLabel(frequency) {
    if (frequency === SEMIMONTHLY)
        return "Twice a month";
    return exports.CADENCE_LABELS[frequency] ?? frequency;
}
exports.PAY_FREQUENCY_LABELS = {
    weekly: "Every week",
    biweekly: "Every 2 weeks",
    semimonthly: "Twice a month",
    monthly: "Monthly",
    irregular: "Irregular",
};
//# sourceMappingURL=cadence.js.map